import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "flex flex-col justify-between lg:flex-row" }
const _hoisted_4 = { class: "hidden flex-wrap items-center gap-2 md:flex" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "w-full md:mt-3 lg:mt-0 lg:w-64" }
const _hoisted_8 = { class: "space-y-2" }
const _hoisted_9 = { class: "ml-2 flex items-center space-x-4" }
const _hoisted_10 = {
  key: 0,
  class: "flex gap-3 rounded border-t border-gray-200 bg-gray-50 p-2 ring-1 ring-gray-300"
}
const _hoisted_11 = {
  role: "list",
  class: "my-2 space-y-2"
}
const _hoisted_12 = {
  key: 0,
  class: "mt-0.5 flex h-48 flex-col flex-wrap items-center justify-center rounded-b bg-gray-200 font-semibold text-gray-600"
}

import { ref, Ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import moment from 'moment';
import { useI18n } from 'vue-i18n';

import store from '@/store';
import { useUiStore, useFilterStore } from '@/stores';
import { toastServiceError } from '@/services/core/notification';
import CallService from '@/services/api/CallService';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import { downloadCsvFile, downloadXlsxFromCsv, ExportFormat } from '@/utils/spreadsheet';
import { UiExportMenu } from '@/components';

import {
    CallActions,
    CallFilter,
    CallGetters,
    CallModel,
    CallStatus,
    CallType,
    FilterItem,
    LevelModel,
    SortMenuItem,
} from '@/types';

import { CallListFilters, CallDetails, CallListSort } from '@/views/pages/call/components';

import { PhoneIcon, PhoneIncomingIcon, PhoneOutgoingIcon } from '@heroicons/vue/solid';

const perPage = 20;

export default /*@__PURE__*/_defineComponent({
  __name: 'CallPage',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const filterStore = useFilterStore();
const uiStore = useUiStore();

const count = ref(0);
const calls: Ref<CallModel[]> = ref([]);

const currentPage = computed<number>({
    get: () => store.getters[CallGetters.PAGE],
    set: (page: number) => store.dispatch(CallActions.SET_PAGE, page),
});

const search = computed<string>({
    get: () => store.getters[CallGetters.SEARCH],
    set: (value) => store.dispatch(CallActions.SET_SEARCH, value),
});

const sortMenuSelected = computed<SortMenuItem[]>({
    get: () => store.getters[CallGetters.SORT],
    set: (value) => store.dispatch(CallActions.SET_SORT, value),
});

const orderBy = computed(() => sortMenuSelected.value.map((s) => s.direction + s.key).join(','));

const levelFilter = computed<LevelModel | null>(() => filterStore.level);
const dateRange = computed<Array<Date>>({
    get: () => filterStore.dateRange,
    set: (range) => filterStore.setDateRange(range),
});

const filterValue = computed<CallFilter>(() => store.getters[CallGetters.FILTER]);

const type = computed(() => filterValue.value.type?.value ?? null);
const status = computed(() => filterValue.value.status?.value ?? null);
const userLabel = computed(() => filterValue.value.userLabel?.id ?? null);
const media = computed(() => filterValue.value.media?.value ?? null);
const numberClient = computed(() => filterValue.value.numberClient?.number ?? null);
const word = computed(() => filterValue.value.word?.word ?? null);
const user = computed(() => filterValue.value.user?.id ?? null);

const activeFilters = computed<FilterItem[]>(() => store.getters[CallGetters.ACTIVE_FILTERS]);

const tabFilter = computed((): FilterItem | null => {
    if (route.params.tab) {
        const filter = route.params.tab.toString().toUpperCase() as CallStatus;
        if (Object.keys(CallType).includes(filter))
            return { field: 'type', label: '', value: filter };
        if (Object.values(CallStatus).includes(filter))
            return { field: 'status', label: '', value: filter };
    }
    return null;
});

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const formatFilename = (startDate: string, endDate: string) => {
    const start = moment(startDate).format('DD/MM/YYYY');
    const end = moment(endDate).format('DD/MM/YYYY');
    return `${t('dashboard.report.call')} - ${start} - ${end}.csv`;
};

const prepareCallParams = () => {
    if (!levelFilter.value || dateRange.value.length < 2) return null;
    const params = {
        search: search.value,
        startDate: moment.utc(dateRange.value[0]).format('YYYY-MM-DD'),
        endDate: moment.utc(dateRange.value[1]).format('YYYY-MM-DD'),
        type: type.value,
        status: status.value,
        userLabel: userLabel.value,
        media: media.value,
        numberClient: numberClient.value,
        word: word.value,
        user: user.value,
        page: currentPage.value,
        per_page: perPage,
        order_by: orderBy.value,
    };
    const { field, value } = tabFilter.value ?? {};
    if (field === 'type' || field === 'status') {
        params[field] = value != null ? String(value) : null;
    }
    return params;
};

const getCalls = async () => {
    const params = prepareCallParams();
    if (!params) return;
    try {
        await setLoading(true);
        const response = await CallService.getAll({ params });
        calls.value = response.data;
        count.value = parseInt(response.headers['x-pagination-count']);
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const exportCalls = async (format: ExportFormat) => {
    const params = prepareCallParams();
    if (!params) return;

    try {
        await setLoading(true);
        await AnalyticsService.trackingAction(
            format === 'csv' ? 'CallCSVDownload' : 'CallXLSXDownload',
        );
        const response = await CallService.getCsvReport({ params });

        const filename = formatFilename(params.startDate, params.endDate);
        const csv =
            typeof response.data === 'string' ? response.data : JSON.stringify(response.data);

        if (format === 'csv') {
            downloadCsvFile(csv, filename);
            return;
        }

        downloadXlsxFromCsv(csv, filename, t('dashboard.report.call'));
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const applyTabFilter = (value: string) => {
    router.push({ name: 'CallList', params: { tab: value.toLowerCase() } });
};

const showAllCalls = () => {
    router.push({ name: 'CallList' });
};

const handleRemoveSortClick = (item: SortMenuItem) => {
    sortMenuSelected.value.splice(sortMenuSelected.value.indexOf(item), 1);
};

const handleRemoveFilter = (field: string) => {
    store.dispatch(CallActions.CLEAR_FILTER, field);
};

const bulletCallStatusColor = (status: string) => {
    switch (status) {
        case CallStatus.ANSWERED:
            return 'bg-green-600';
        case CallStatus.BUSY:
            return 'bg-yellow-500';
        case CallStatus.NO_ANSWER:
            return 'bg-red-500';
        default:
            return 'bg-gray-600';
    }
};

watch(
    [
        search,
        levelFilter,
        dateRange,
        orderBy,
        status,
        userLabel,
        type,
        media,
        numberClient,
        user,
        word,
        tabFilter,
    ],
    () => {
        if (currentPage.value === 1) getCalls();
        else currentPage.value = 1;
    },
);

watch(currentPage, () => {
    getCalls();
});

if (route.query.clearStateOnSetup) {
    currentPage.value = 1;
    router.replace({ query: {} });
}

onMounted(async () => {
    await getCalls();
});

return (_ctx: any,_cache: any) => {
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiDatePicker = _resolveComponent("UiDatePicker")!
  const _component_UiListSearch = _resolveComponent("UiListSearch")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiSortMenuBadge = _resolveComponent("UiSortMenuBadge")!
  const _component_UiBadge = _resolveComponent("UiBadge")!
  const _component_UiPagination = _resolveComponent("UiPagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('dashboard.calls')), 1)
      ]),
      actions: _withCtx(() => [
        _createVNode(_unref(UiExportMenu), {
          label: "Download",
          onSelect: exportCalls
        })
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (showAllCalls())),
          class: _normalizeClass([tabFilter.value === null ? 'bg-gray-50' : 'bg-gray-200', "flex cursor-pointer items-center rounded-xl px-2 py-1 text-sm shadow ring-1 ring-gray-300 hover:bg-white"])
        }, [
          _createVNode(_unref(PhoneIcon), {
            class: "mr-1 h-4 w-4 text-gray-500",
            "aria-hidden": "true"
          }),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('call.filters.allTypesAndStatus')), 1)
        ], 2),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(CallType), (type) => {
          return (_openBlock(), _createElementBlock("div", {
            key: `filterBar${type}`,
            onClick: ($event: any) => (applyTabFilter(type)),
            class: _normalizeClass(["flex cursor-pointer items-center rounded-xl px-2 py-1 text-sm shadow ring-1 ring-gray-300 hover:bg-white", 
                            tabFilter.value !== null && tabFilter.value?.value === type
                                ? 'bg-gray-50'
                                : 'bg-gray-200'
                        ])
          }, [
            (type === _unref(CallType).INBOUND)
              ? (_openBlock(), _createBlock(_unref(PhoneIncomingIcon), {
                  key: 0,
                  class: "mr-1 h-4 w-4 text-cyan-500",
                  "aria-hidden": "true"
                }))
              : (type === _unref(CallType).OUTBOUND)
                ? (_openBlock(), _createBlock(_unref(PhoneOutgoingIcon), {
                    key: 1,
                    class: "mr-1 h-4 w-4 text-orange-500",
                    "aria-hidden": "true"
                  }))
                : (type === _unref(CallType).INTERNAL)
                  ? (_openBlock(), _createBlock(_unref(PhoneIcon), {
                      key: 2,
                      class: "mr-1 h-4 w-4 text-gray-500",
                      "aria-hidden": "true"
                    }))
                  : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(_ctx.$t(`call.type.${type}`)), 1)
          ], 10, _hoisted_5))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Object.values(_unref(CallStatus)), (status) => {
          return (_openBlock(), _createElementBlock("div", {
            key: `filterBar${status}`,
            onClick: ($event: any) => (applyTabFilter(status)),
            class: _normalizeClass(["flex cursor-pointer items-center rounded-xl px-2 py-1 text-sm shadow ring-opacity-5 hover:bg-white", 
                            tabFilter.value !== null && tabFilter.value?.value === status
                                ? 'bg-gray-50'
                                : 'bg-gray-200'
                        ])
          }, [
            _createElementVNode("div", {
              class: _normalizeClass(["mr-1 flex h-2.5 w-2.5 rounded-full", bulletCallStatusColor(status)])
            }, null, 2),
            _createTextVNode(" " + _toDisplayString(_ctx.$t(`call.status.${status}`)), 1)
          ], 10, _hoisted_6))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createVNode(_component_UiDatePicker, {
          modelValue: dateRange.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dateRange).value = $event)),
          range: "",
          multiCalendars: ""
        }, null, 8, ["modelValue"])
      ])
    ]),
    _createElementVNode("div", _hoisted_8, [
      _createVNode(_component_UiPanel, {
        class: "flex px-4 py-2",
        "no-padding": ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_UiListSearch, {
            modelValue: search.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((search).value = $event))
          }, null, 8, ["modelValue"]),
          _createElementVNode("div", _hoisted_9, [
            _createVNode(_unref(CallListSort), {
              modelValue: sortMenuSelected.value,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((sortMenuSelected).value = $event))
            }, null, 8, ["modelValue"]),
            _createVNode(_unref(CallListFilters))
          ])
        ]),
        _: 1
      }),
      (sortMenuSelected.value.length || activeFilters.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortMenuSelected.value, (selected) => {
              return (_openBlock(), _createBlock(_component_UiSortMenuBadge, {
                key: selected.key,
                item: selected,
                onRemove: ($event: any) => (handleRemoveSortClick(selected))
              }, null, 8, ["item", "onRemove"]))
            }), 128)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeFilters.value, (filter) => {
              return (_openBlock(), _createBlock(_component_UiBadge, {
                key: filter,
                removable: "",
                onRemove: ($event: any) => (handleRemoveFilter(filter.field))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t(`call.filters.activeTag.${filter.field}`)) + ": " + _toDisplayString(filter.label), 1)
                ]),
                _: 2
              }, 1032, ["onRemove"]))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("ul", _hoisted_11, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calls.value, (call) => {
          return (_openBlock(), _createBlock(_unref(CallDetails), {
            key: call.id,
            call: call,
            class: "hover:bg-white"
          }, null, 8, ["call"]))
        }), 128)),
        (!calls.value.length)
          ? (_openBlock(), _createElementBlock("li", _hoisted_12, _toDisplayString(_ctx.$t('core.messages.noRecordsFound')), 1))
          : _createCommentVNode("", true)
      ]),
      (calls.value.length)
        ? (_openBlock(), _createBlock(_component_UiPagination, {
            key: 1,
            modelValue: currentPage.value,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((currentPage).value = $event)),
            count: count.value,
            perPage: perPage
          }, null, 8, ["modelValue", "count"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})